import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";

type EquityPoint = { t: number; equity: number };

/**
 * Live equity sparkline for a single leg of a hedge pair.
 *
 * X axis  = elapsed seconds since the chart mounted (or since the most
 *           recent live tick — whichever is shorter once the buffer
 *           fills). Time-based, not index-based, so visible motion
 *           tracks the WS heartbeat rather than the React render cadence.
 * Y axis  = USD. Auto-scaled to fit equity + every reference floor that
 *           is within sight, with ~2% padding so the line never kisses
 *           the gridlines.
 *
 * Reference lines (drawn at fixed dollar levels):
 *   • Start Balance — current `balance` from the broker footer. Anchor
 *     for "where am I relative to flat?".
 *   • Daily DD Floor — `currentEquity - (dailyDdLimit - dailyDdUsed)`.
 *     The equity level that, if touched, breaches today's daily DD.
 *     Recomputed on every render so it tracks the daily-used counter.
 *   • Max DD Floor — `currentEquity - (totalDdLimit - totalDdUsed)`.
 *     Same idea but for the lifetime drawdown.
 *   • Profit Target — optional, only rendered when the operator has
 *     captured the firm's `profitTargetPct` for this account. Drawn
 *     above the equity line; tells the operator how far they are from
 *     payout / phase pass.
 *
 * The component is INTENTIONALLY zero-config beyond the props: no
 * firm-specific behavior, no per-symbol logic. It's reusable across
 * any hedge leg / standalone account view that has equity + DD limits.
 *
 * Empty state: when both `liveEquity` and the buffered history are
 * null/empty (REST snapshot only, WS hasn't ticked yet), we still
 * render a skeleton track + reference lines so the card height
 * doesn't pop in when the first tick arrives.
 */
export function AccountEquityChart({
  startBalance,
  liveEquity,
  isLive,
  dailyDdLimit,
  dailyDdUsed,
  totalDdLimit,
  totalDdUsed,
  profitTargetUsd = null,
  side,
  className,
}: {
  /** Snapshot of broker `balance`. Treated as the flat-PnL anchor. */
  startBalance: number | null;
  /** Latest equity tick from the WS, or null when only REST is available. */
  liveEquity: number | null;
  /** True while the pair WS is connected. Drives the "live dot" in the header. */
  isLive: boolean;
  /** Remaining daily DD budget = limit - used. Used to derive the daily floor. */
  dailyDdLimit: number;
  dailyDdUsed: number;
  /** Remaining lifetime DD budget = limit - used. Drives the max-DD floor. */
  totalDdLimit: number;
  totalDdUsed: number;
  /** Absolute USD equity that, when reached, hits the firm's profit target.
   *  Null = firm has no published target or the rule isn't captured for this
   *  account. The line is hidden when null. */
  profitTargetUsd?: number | null;
  /** Visual hint — "long" leg uses emerald accent for the equity line, "short"
   *  uses rose. Reference lines stay neutral so the eye lands on the live line. */
  side: "long" | "short";
  className?: string;
}): ReactNode {
  // ── Tick buffer ─────────────────────────────────────────────
  // Hold the last N (default 120) live equity readings locally.
  // Capacity is small on purpose: this is a *live* sparkline, not a
  // long-horizon history view. For day/week analytics use the trade
  // history table elsewhere on the page.
  const CAPACITY = 120;
  const [history, setHistory] = useState<EquityPoint[]>([]);
  const lastSeenEquity = useRef<number | null>(null);

  useEffect(() => {
    if (liveEquity == null) return;
    // Only push when the value actually changes — WS tick frames are
    // sometimes idempotent (no equity delta). Same-value pushes would
    // flatten the visible curve.
    if (lastSeenEquity.current === liveEquity) return;
    lastSeenEquity.current = liveEquity;
    setHistory((prev) => {
      const next = [...prev, { t: Date.now(), equity: liveEquity }];
      if (next.length > CAPACITY) next.shift();
      return next;
    });
  }, [liveEquity]);

  // Seed with a single point so the chart always has something to draw.
  // Without this the first render before the WS settles produces a blank
  // strip with floating reference lines and no visible curve.
  const seededHistory: EquityPoint[] =
    history.length > 0
      ? history
      : liveEquity != null
        ? [{ t: Date.now(), equity: liveEquity }]
        : startBalance != null
          ? [{ t: Date.now(), equity: startBalance }]
          : [];

  // ── Reference levels ────────────────────────────────────────
  const equity = liveEquity ?? startBalance ?? 0;
  const dailyRemaining = Math.max(0, dailyDdLimit - dailyDdUsed);
  const totalRemaining = Math.max(0, totalDdLimit - totalDdUsed);
  const dailyFloor = equity - dailyRemaining;
  const totalFloor = equity - totalRemaining;

  // ── Y domain ────────────────────────────────────────────────
  // Take min/max across the equity curve + every visible reference,
  // then pad ~2% so the lines breathe.
  const collected: number[] = [];
  for (const p of seededHistory) collected.push(p.equity);
  if (startBalance != null) collected.push(startBalance);
  if (Number.isFinite(dailyFloor)) collected.push(dailyFloor);
  if (Number.isFinite(totalFloor)) collected.push(totalFloor);
  if (profitTargetUsd != null) collected.push(profitTargetUsd);

  let yMin = collected.length ? Math.min(...collected) : 0;
  let yMax = collected.length ? Math.max(...collected) : 1;
  if (yMin === yMax) {
    // Single-value chart — fabricate a tiny window so the line is
    // visible. Without this Recharts collapses the whole plot area
    // to the centerline.
    yMin = yMin - Math.max(1, Math.abs(yMin) * 0.005);
    yMax = yMax + Math.max(1, Math.abs(yMax) * 0.005);
  } else {
    const span = yMax - yMin;
    yMin = yMin - span * 0.05;
    yMax = yMax + span * 0.05;
  }

  // ── Line colours ────────────────────────────────────────────
  // Use the same emerald/rose pairing as the leg badges in the
  // positions section so the operator's eye links the chart back
  // to the correct leg without having to re-read the title.
  const equityStroke =
    side === "long" ? "var(--color-emerald, #10b981)" : "var(--color-rose, #f43f5e)";

  // Empty state — render reference lines on a flat axis so the card
  // height matches the populated state. Avoids layout jumps when the
  // first tick arrives.
  if (seededHistory.length === 0) {
    return (
      <div
        className={cn(
          "flex h-32 items-center justify-center rounded-lg border border-border/40 bg-muted/10 text-[11px] text-muted-foreground",
          className,
        )}
      >
        Waiting for live equity…
      </div>
    );
  }

  return (
    <div className={cn("rounded-lg border border-border/40 bg-muted/10 p-2", className)}>
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Equity Track
        </span>
        <span
          className={cn(
            "font-mono text-[10px] tabular-nums",
            isLive ? "text-accent" : "text-muted-foreground/70",
          )}
        >
          {fmtMoney(equity)}
          {isLive && <span className="ml-1.5 text-accent">●</span>}
        </span>
      </div>
      <div className="h-28 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={seededHistory}
            margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
          >
            <defs>
              <linearGradient id={`eq-fill-${side}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={equityStroke} stopOpacity={0.25} />
                <stop offset="100%" stopColor={equityStroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="currentColor"
              strokeOpacity={0.08}
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              hide
            />
            <YAxis
              domain={[yMin, yMax]}
              hide
            />
            <Tooltip
              cursor={{ stroke: "currentColor", strokeOpacity: 0.2 }}
              contentStyle={{
                background: "var(--background)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 11,
                padding: "6px 10px",
              }}
              labelFormatter={(t) => {
                const ts = typeof t === "number" ? t : Number(t);
                if (!Number.isFinite(ts)) return "";
                return new Date(ts).toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                });
              }}
              formatter={(v) => {
                const n = typeof v === "number" ? v : Number(v);
                return [Number.isFinite(n) ? fmtMoney(n) : "—", "Equity"];
              }}
            />

            {/* Reference floors / ceilings — drawn before the line so the
                live track sits on top. */}
            {startBalance != null && (
              <ReferenceLine
                y={startBalance}
                stroke="currentColor"
                strokeOpacity={0.4}
                strokeDasharray="3 3"
                label={{
                  value: `Start ${fmtMoney(startBalance)}`,
                  position: "insideTopLeft",
                  fill: "currentColor",
                  fillOpacity: 0.55,
                  fontSize: 9,
                }}
              />
            )}
            {Number.isFinite(dailyFloor) && (
              <ReferenceLine
                y={dailyFloor}
                stroke="rgb(245 158 11)"
                strokeOpacity={0.7}
                strokeDasharray="4 3"
                label={{
                  value: `Daily DD ${fmtMoney(dailyFloor)}`,
                  position: "insideBottomLeft",
                  fill: "rgb(245 158 11)",
                  fontSize: 9,
                }}
              />
            )}
            {Number.isFinite(totalFloor) && (
              <ReferenceLine
                y={totalFloor}
                stroke="rgb(244 63 94)"
                strokeOpacity={0.7}
                strokeDasharray="6 3"
                label={{
                  value: `Max DD ${fmtMoney(totalFloor)}`,
                  position: "insideBottomLeft",
                  fill: "rgb(244 63 94)",
                  fontSize: 9,
                }}
              />
            )}
            {profitTargetUsd != null && (
              <ReferenceLine
                y={profitTargetUsd}
                stroke="rgb(16 185 129)"
                strokeOpacity={0.6}
                strokeDasharray="2 4"
                label={{
                  value: `Target ${fmtMoney(profitTargetUsd)}`,
                  position: "insideTopLeft",
                  fill: "rgb(16 185 129)",
                  fontSize: 9,
                }}
              />
            )}

            {/* Soft area fill under the equity curve for visual weight. */}
            <Area
              type="monotone"
              dataKey="equity"
              stroke="none"
              fill={`url(#eq-fill-${side})`}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="equity"
              stroke={equityStroke}
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
